import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import {
  ArrowLeft,
  Trophy,
  Sparkles,
  Star,
  Heart,
  Crown,
  Loader2,
  Flame,
} from "lucide-react";

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/social/leaderboard")
      .then((r) => setLeaderboard(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getMedalColor = (index) => {
    if (index === 0) return "from-[#FFD700] to-[#FFA000]";
    if (index === 1) return "from-[#C0C0C0] to-[#888]";
    if (index === 2) return "from-[#CD7F32] to-[#8B4513]";
    return "from-white/10 to-white/5";
  };

  const getMedalIcon = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-[#070B14]" />;
    if (index === 1) return <Star className="w-5 h-5 text-[#070B14]" />;
    if (index === 2) return <Flame className="w-5 h-5 text-[#070B14]" />;
    return <Heart className="w-4 h-4 text-white/40" />;
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: "#070B14" }}>
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#070B14]/80 border-b border-white/5">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/friends")} className="text-white/60 hover:text-white" data-testid="leaderboard-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="font-display text-xl text-white">Cosmic Match Leaderboard</h1>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Hero */}
        <div className="text-center py-6 space-y-2">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h2 className="font-display text-2xl text-white">Who's the Cosmic Match?</h2>
          <p className="text-white/50 font-body text-sm max-w-sm mx-auto">
            Top compatibility scores among friends. Add more friends to climb the leaderboard!
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12 glass-card p-8">
            <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 font-body">No compatibility data yet</p>
            <p className="text-white/40 font-body text-sm mt-1">Add friends and check your compatibility to appear here!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {leaderboard.map((entry, i) => (
              <div
                key={i}
                className={`glass-card p-4 flex items-center gap-4 transition-all ${
                  i < 3 ? "border border-[#D4AF37]/20" : ""
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`leaderboard-entry-${i}`}
              >
                {/* Rank */}
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getMedalColor(i)} flex items-center justify-center flex-shrink-0`}>
                  {i < 3 ? getMedalIcon(i) : <span className="text-white/60 font-display text-sm">{i + 1}</span>}
                </div>

                {/* Names */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-body font-medium text-sm truncate">
                    <span className="text-[#C4B5FD]">{entry.user_name}</span>
                    <span className="text-white/30 mx-1.5">&</span>
                    <span className="text-[#D4AF37]">{entry.friend_name}</span>
                  </p>
                  <p className="text-white/40 font-body text-xs mt-0.5">
                    {entry.element_match}
                  </p>
                </div>

                {/* Score */}
                <div className="text-right flex-shrink-0">
                  <p className={`font-display text-2xl ${i === 0 ? "text-[#FFD700]" : i === 1 ? "text-[#C0C0C0]" : i === 2 ? "text-[#CD7F32]" : "text-white/80"}`}>
                    {entry.score}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
